import { Plus, Trash2 } from "lucide-react";

import PropertyPicker from "./PropertyPicker";

/**
 * Editor for a nested filter set: groups of AND conditions, OR'd together.
 *
 * This shape is the point of the whole builder. HubSpot's report filters nest,
 * and a flat AND cannot express them — report 369308847 qualifies a client
 * service if it matches any ONE of three rule groups. Approximating that
 * returns a plausible number that quietly disagrees with HubSpot.
 */

const inputClass =
  "rounded border border-[#cccccc] bg-white px-2.5 py-1.5 text-sm text-[#323c44] transition-colors hover:border-[#7d8b96] focus:border-[#57c1a9] focus:outline-none";

function valueInputKind(operators, operatorName) {
  return operators.find((o) => o.value === operatorName)?.value_input || "single";
}

function ConditionRow({ condition, properties, operators, onChange, onRemove }) {
  const property = properties.find((p) => p.name === condition.property);
  const options = property?.options || [];
  const inputKind = valueInputKind(operators, condition.operator);
  const values = Array.isArray(condition.value) ? condition.value : [condition.value ?? ""];

  const setValueAt = (index, next) => {
    const updated = [...values];
    updated[index] = next;
    onChange({ ...condition, value: updated });
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="filter-condition">
      <div className="min-w-[190px] flex-1">
        <PropertyPicker
          properties={properties}
          value={condition.property || ""}
          onChange={(name) => onChange({ ...condition, property: name })}
          testId="condition-property"
        />
      </div>

      <select
        className={`${inputClass} min-w-[150px]`}
        value={condition.operator || "eq"}
        onChange={(event) => {
          const operator = event.target.value;
          const kind = valueInputKind(operators, operator);
          // Reshape the value to whatever the new operator expects, so
          // switching from "is any of" to "is before" cannot leave a list
          // behind where the evaluator wants a scalar.
          let value;
          if (kind === "none") value = undefined;
          else if (kind === "multi") value = values.filter(Boolean);
          else if (kind === "range") value = [values[0] ?? "", values[1] ?? ""];
          else value = values[0] ?? "";
          onChange({ ...condition, operator, value });
        }}
        aria-label="Operator"
      >
        {operators.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>

      {inputKind === "none" ? null : inputKind === "multi" ? (
        options.length ? (
          <div className="max-h-28 min-w-[190px] flex-1 overflow-y-auto rounded border border-[#cccccc] bg-white p-1.5">
            {options.map((option) => (
              <label
                key={option.value}
                className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm text-[#323c44] hover:bg-[#f5f5f5]"
              >
                <input
                  type="checkbox"
                  checked={values.includes(option.value)}
                  onChange={(event) =>
                    onChange({
                      ...condition,
                      value: event.target.checked
                        ? [...values.filter(Boolean), option.value]
                        : values.filter((v) => v !== option.value),
                    })
                  }
                />
                <span className="truncate">{option.label}</span>
              </label>
            ))}
          </div>
        ) : (
          <input
            className={`${inputClass} min-w-[190px] flex-1`}
            value={values.filter(Boolean).join(", ")}
            onChange={(event) =>
              onChange({
                ...condition,
                value: event.target.value.split(",").map((v) => v.trim()).filter(Boolean),
              })
            }
            placeholder="Values, comma separated"
            aria-label="Values"
          />
        )
      ) : inputKind === "range" ? (
        <>
          <input
            className={`${inputClass} w-28`}
            value={values[0] ?? ""}
            onChange={(event) => setValueAt(0, event.target.value)}
            placeholder="From"
            aria-label="From"
          />
          <input
            className={`${inputClass} w-28`}
            value={values[1] ?? ""}
            onChange={(event) => setValueAt(1, event.target.value)}
            placeholder="To"
            aria-label="To"
          />
        </>
      ) : options.length ? (
        <select
          className={`${inputClass} min-w-[190px] flex-1`}
          value={values[0] ?? ""}
          onChange={(event) => onChange({ ...condition, value: event.target.value })}
          aria-label="Value"
        >
          <option value="">Choose a value…</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          className={`${inputClass} min-w-[190px] flex-1`}
          value={values[0] ?? ""}
          onChange={(event) => onChange({ ...condition, value: event.target.value })}
          placeholder="Value (dates as YYYY-MM-DD or DD/MM/YYYY)"
          aria-label="Value"
        />
      )}

      <button
        type="button"
        onClick={onRemove}
        className="rounded p-1.5 text-[#7d8b96] hover:bg-red-50 hover:text-red-600"
        title="Remove condition"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

/**
 * Filter editor: numbered rules combined by an expression.
 *
 * This is HubSpot's own model, and it is the model because rules get shared.
 * Report 369308847 reads (1 AND 2) OR (1 AND 3) OR 4 — rule 1 appears in two
 * branches. Flattening that into independent OR groups means storing rule 1
 * twice, and a rule stored twice is one that eventually gets edited once.
 *
 * A definition saved in the older OR-of-ANDs shape is shown as numbered rules
 * joined by OR, which is the same thing, and saves forward in the new shape.
 */
export default function FilterGroupEditor({ filters, properties = [], operators = [], onChange }) {
  // Legacy `groups` are just rules OR'd together.
  const legacy = !filters?.rules && filters?.groups?.length;
  const rules = filters?.rules
    || (filters?.groups || []).map((group, index) => ({ id: index + 1, ...group }));
  const expression = filters?.expression
    ?? (legacy ? rules.map((r) => r.id).join(" OR ") : "");

  const update = (nextRules, nextExpression = expression) =>
    onChange({ rules: nextRules, expression: nextExpression });

  const patchGroup = (groupIndex, nextGroup) =>
    update(rules.map((g, i) => (i === groupIndex ? nextGroup : g)));

  const nextId = () => (rules.length ? Math.max(...rules.map((r) => r.id || 0)) + 1 : 1);

  const referenced = new Set(
    (expression.match(/\d+/g) || []).map(Number),
  );
  const unapplied = expression.trim()
    ? rules.map((r) => r.id).filter((id) => !referenced.has(id))
    : [];

  const groups = rules;

  return (
    <div className="space-y-3" data-testid="filter-group-editor">
      {groups.length === 0 && (
        <p className="rounded border border-dashed border-[#cccccc] px-3 py-6 text-center text-sm text-[#4a5b68]">
          No filters — this report counts every record.
        </p>
      )}

      {/* How the rules combine. Free text, matching HubSpot's own box, so a
          shared rule can appear in more than one branch. */}
      {groups.length > 1 && (
        <div className="rounded border border-[#cccccc] bg-[#f5f5f5] p-3">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-[#4a5b68]">
              How the rules combine
            </span>
            <input
              className="w-full rounded border border-[#cccccc] bg-white px-2.5 py-1.5 font-mono text-sm text-[#323c44] focus:border-[#57c1a9] focus:outline-none"
              value={expression}
              onChange={(event) => update(rules, event.target.value)}
              placeholder={rules.map((r) => r.id).join(" OR ")}
              data-testid="filter-expression"
            />
          </label>
          <p className="mt-1.5 text-[11px] leading-snug text-[#7d8b96]">
            Rule numbers with AND, OR, NOT and brackets — for example{" "}
            <code className="rounded bg-white px-1">(1 AND 2) OR (1 AND 3) OR 4</code>. A rule can
            appear more than once. Leave blank and every rule must hold.
          </p>
          {unapplied.length > 0 && (
            <p className="mt-2 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800">
              Rule{unapplied.length > 1 ? "s" : ""} {unapplied.join(", ")}{" "}
              {unapplied.length > 1 ? "are" : "is"} defined but never used by this expression, so{" "}
              {unapplied.length > 1 ? "they are" : "it is"} not being applied.
            </p>
          )}
        </div>
      )}

      {groups.map((group, groupIndex) => (
        <div key={groupIndex}>
          <div className="rounded border border-[#cccccc] bg-[#f5f5f5] p-3">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#4a5b68]">
                <span className="mr-1.5 inline-flex h-5 w-5 items-center justify-center rounded bg-[#323c44] text-[11px] font-bold text-white">
                  {group.id}
                </span>
                Rule {group.id} — all must be true
                {expression.trim() && !referenced.has(group.id) && (
                  <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold normal-case tracking-normal text-amber-800">
                    not applied
                  </span>
                )}
              </p>
              <button
                type="button"
                onClick={() => {
                  const removed = group.id;
                  const remaining = rules.filter((_, i) => i !== groupIndex);
                  // Drop the rule from the expression too, otherwise it names
                  // a rule that no longer exists and the filter is refused.
                  const cleaned = remaining.length
                    ? (referenced.has(removed)
                        ? remaining.map((r) => r.id).join(" OR ")
                        : expression)
                    : "";
                  update(remaining, cleaned);
                }}
                className="rounded p-1 text-[#7d8b96] hover:bg-red-50 hover:text-red-600"
                title="Remove rule"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-2">
              {(group.conditions || []).map((condition, conditionIndex) => (
                <ConditionRow
                  key={conditionIndex}
                  condition={condition}
                  properties={properties}
                  operators={operators}
                  onChange={(next) =>
                    patchGroup(groupIndex, {
                      ...group,
                      conditions: group.conditions.map((c, i) => (i === conditionIndex ? next : c)),
                    })
                  }
                  onRemove={() =>
                    patchGroup(groupIndex, {
                      ...group,
                      conditions: group.conditions.filter((_, i) => i !== conditionIndex),
                    })
                  }
                />
              ))}
            </div>

            <button
              type="button"
              onClick={() =>
                patchGroup(groupIndex, {
                  ...group,
                  conditions: [...(group.conditions || []), { property: "", operator: "eq", value: "" }],
                })
              }
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#4a5b68] hover:text-[#119996]"
            >
              <Plus className="h-3.5 w-3.5" /> Add condition
            </button>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={() => {
          const id = nextId();
          const nextRules = [...rules, { id, conditions: [{ property: "", operator: "eq", value: "" }] }];
          // A new rule joins the expression, so adding one is never a no-op.
          update(nextRules, expression.trim() ? `${expression} OR ${id}` : "");
        }}
        className="inline-flex items-center gap-1.5 rounded-full border border-[#cccccc] bg-white px-3 py-1.5 text-xs font-semibold text-[#323c44] hover:border-[#57c1a9] hover:text-[#119996]"
        data-testid="add-filter-group"
      >
        <Plus className="h-3.5 w-3.5" /> {groups.length ? "Add a rule" : "Add a filter rule"}
      </button>
    </div>
  );
}
